import { Shield, ShieldCheck, Star, Crown } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export type TrustLevel = 1 | 2 | 3 | 4 | 5;

interface TrustBadgeProps {
  level: TrustLevel;
  postCount?: number;
  salesCount?: number;
  showLabel?: boolean;
  size?: "sm" | "md" | "lg";
}

const trustLevels = {
  1: {
    label: "새싹",
    icon: Shield,
    color: "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600",
    description: "신규 회원",
  },
  2: {
    label: "일반",
    icon: Shield,
    color: "bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400 border-blue-300 dark:border-blue-700",
    description: "일반 회원",
  },
  3: {
    label: "활동",
    icon: ShieldCheck,
    color: "bg-green-50 dark:bg-green-950 text-green-600 dark:text-green-400 border-green-300 dark:border-green-700",
    description: "활동 회원",
  },
  4: {
    label: "신뢰",
    icon: Star,
    color: "bg-purple-50 dark:bg-purple-950 text-purple-600 dark:text-purple-400 border-purple-300 dark:border-purple-700",
    description: "신뢰 회원",
  },
  5: {
    label: "우수",
    icon: Crown,
    color: "bg-amber-50 dark:bg-amber-950 text-amber-600 dark:text-amber-400 border-amber-300 dark:border-amber-700",
    description: "우수 회원",
  },
} as const;

export function calculateTrustLevel(postCount: number, salesCount: number = 0): TrustLevel {
  const totalActivity = postCount + (salesCount * 3); // 판매는 3배 가중치

  if (totalActivity >= 51 || salesCount >= 11) return 5;
  if (totalActivity >= 31 || salesCount >= 4) return 4;
  if (totalActivity >= 16 || salesCount >= 1) return 3;
  if (totalActivity >= 6) return 2;
  return 1;
}

export function TrustBadge({
  level,
  postCount = 0,
  salesCount = 0,
  showLabel = true,
  size = "sm"
}: TrustBadgeProps) {
  const trustInfo = trustLevels[level];
  const Icon = trustInfo.icon;

  const iconSize = {
    sm: "h-3 w-3",
    md: "h-4 w-4",
    lg: "h-5 w-5",
  }[size];

  const badgeSize = {
    sm: "text-xs px-1.5 py-0.5",
    md: "text-sm px-2 py-1",
    lg: "text-base px-3 py-1.5",
  }[size];

  return (
    <Badge
      variant="outline"
      className={`${trustInfo.color} ${badgeSize} gap-1 font-medium`}
      data-testid={`badge-trust-level-${level}`}
      title={`${trustInfo.description} (게시글: ${postCount}, 판매완료: ${salesCount})`}
    >
      <Icon className={iconSize} />
      {showLabel && <span>{trustInfo.label}</span>}
    </Badge>
  );
}
